import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Link,
  Typography
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import StatusChip from '../shared/StatusChip';
import UrgencyChip from '../shared/UrgencyChip';
import SupportTypeChip from '../shared/SupportTypeChip';
import { formatDate } from '../../utils/ticketTransformers';

/**
 * Ticket table component for desktop view
 */
const TicketTable = ({
  tickets,
  totalTickets,
  page,
  rowsPerPage,
  orderBy,
  order,
  locations = [],
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onViewTicket,
  onEditTicket
}) => {
  const { t } = useThemeSettings();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const columns = [
    { id: 'id', label: t ? t('Tickets.id') : 'ID', sortable: true, width: 60 },
    { id: 'title', label: t ? t('Tickets.title') : 'Title', sortable: true },
    { id: 'status', label: t ? t('Tickets.status') : 'Status', sortable: true, width: 100 },
    { id: 'urgency', label: t ? t('Tickets.urgency') : 'Urgency', sortable: true, width: 90 },
    { id: 'support_type', label: t ? t('Tickets.support_type') : 'Type', sortable: true, width: 120 },
    { id: 'next_step', label: t ? t('VisualTreeView.next_step') : 'Next Step', sortable: true },
    { id: 'location', label: t ? t('Tickets.location') : 'Location', sortable: true },
    { id: 'device', label: t ? t('Tickets.device') : 'Device', sortable: true },
    { id: 'createdAt', label: t ? t('Tickets.created') : 'Created', sortable: true, width: 130 },
    { id: 'actions', label: t ? t('Tickets.actions') : 'Actions', sortable: false, width: 100 }
  ];

  const handleLocationClick = (locationId) => {
    if (locationId) {
      navigate(`/locations?selectedId=${locationId}`);
    }
  };

  const handleDeviceClick = (deviceId) => {
    if (deviceId) {
      navigate(`/devices?selectedId=${deviceId}`);
    }
  };

  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ width: column.width, fontWeight: 'bold' }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => onSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow
                key={ticket.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onViewTicket(ticket)}
              >
                <TableCell>{ticket.id}</TableCell>
                <TableCell>
                  <Box sx={{ maxWidth: 300 }}>
                    {ticket.title || ticket.description?.substring(0, 50)}
                    {ticket.description?.length > 50 && '...'}
                  </Box>
                </TableCell>
                <TableCell><StatusChip status={ticket.status} /></TableCell>
                <TableCell><UrgencyChip urgency={ticket.urgency} /></TableCell>
                <TableCell><SupportTypeChip type={ticket.support_type} /></TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 150,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={ticket.next_step || ''}
                  >
                    {ticket.next_step || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(() => {
                    // Get location name - first try from ticket object, then lookup from locations array
                    const locationId = ticket.locationId || ticket.location_id || ticket.location?.id;

                    // Try to find location from locations array - compare both as numbers and strings
                    const foundLocation = locations?.find(l =>
                      l.id === locationId ||
                      l.id === parseInt(locationId) ||
                      String(l.id) === String(locationId)
                    );

                    // Get friendlyName from ticket, but check if it's a real name or just a fallback
                    const ticketLocationName = ticket.location?.friendlyName || ticket.location?.name;
                    const isFallbackName = ticketLocationName && ticketLocationName.startsWith('Location ');

                    // Prefer the lookup result, fall back to ticket data only if lookup fails and it's not a fallback
                    const locationName = foundLocation?.friendlyName ||
                      (!isFallbackName ? ticketLocationName : null);

                    return locationName ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLocationClick(locationId);
                        }}
                        sx={{ textAlign: 'left' }}
                      >
                        {locationName}
                      </Link>
                    ) : '-';
                  })()}
                </TableCell>
                <TableCell>
                  {(ticket.related_device_name || ticket.device?.friendlyName) ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeviceClick(ticket.related_device_id || ticket.device?.id);
                      }}
                      sx={{ textAlign: 'left' }}
                    >
                      {ticket.related_device_name || ticket.device?.friendlyName}
                    </Link>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{formatDate(ticket.createdAt || ticket.created_at)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={t ? t('Tickets.view') : 'View'}>
                    <IconButton size="small" onClick={() => onViewTicket(ticket)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {hasPermission('tickets.edit') && (
                    <Tooltip title={t ? t('Tickets.edit') : 'Edit'}>
                      <IconButton size="small" onClick={() => onEditTicket(ticket)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {tickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  {t ? t('Tickets.no_tickets') : 'No tickets found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalTickets}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        labelRowsPerPage={t ? t('Tickets.rows_per_page') : 'Rows per page'}
      />
    </Paper>
  );
};

export default TicketTable;
